import type { BlastRecipientDto } from '@/types/emailBlast'
import { lowerSafe } from '@/utils/strings'

// Sorting rules for the blast recipient grid (ORB-706). Its own module rather than a helper inside the
// section: exporting a function from a component file breaks fast refresh, and this is the only real
// logic on that screen, so it is worth being separately testable anyway.

/** True once the row reached delivery — the condition under which its counts mean anything. */
export function reachedRecipient(recipient: BlastRecipientDto): boolean {
  return !!(recipient.DeliveredAtUtc || recipient.FirstOpenedAtUtc || recipient.FirstClickedAtUtc)
}

/**
 * Opens and clicks sort on the number the row actually DISPLAYS. A never-delivered row renders "—"
 * rather than 0, so sorting it as 0 would interleave dashes among real zeroes and make the order
 * unreadable. It ranks BELOW zero instead — it did not engage least, it never had the chance.
 *
 * Which means it groups at the top ascending ("fewest first") and at the bottom descending ("most
 * first"), and that is the whole story. It cannot be pinned to the bottom in both directions: the
 * shared grid sorts ascending with the comparator and reverses the array for descending, so anything
 * a comparator does is symmetric by construction. Changing that would mean making DataGrid's sort
 * direction-aware, which is a change to nine other grids and not obviously an improvement — "least
 * engaged first" is a reasonable reading of an ascending engagement sort.
 */
function engagementValue(recipient: BlastRecipientDto, count: number): number {
  return reachedRecipient(recipient) ? count : -1
}

export function getRecipientComparator(key: string): (a: BlastRecipientDto, b: BlastRecipientDto) => number {
  switch (key) {
    case 'status':
      return (a, b) => lowerSafe(a.Status).localeCompare(lowerSafe(b.Status))
    case 'opens':
      return (a, b) => engagementValue(a, a.OpenCount) - engagementValue(b, b.OpenCount)
    case 'clicks':
      return (a, b) => engagementValue(a, a.ClickCount) - engagementValue(b, b.ClickCount)
    default:
      // The cell renders the name when there is one, so sorting on the address would reorder rows
      // against what the reader can see.
      return (a, b) =>
        lowerSafe(a.RecipientName || a.RecipientEmail).localeCompare(lowerSafe(b.RecipientName || b.RecipientEmail))
  }
}
